import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useRepositories } from '../contexts/RepositoriesContext'
import { useNotification } from '../contexts/NotificationContext'
import { getSessionToken } from '../auth/session'
import {
  applyInstallDataCenter,
  clearPendingGitHubInstallation,
  connectGitHubInstallation,
  detectGitHubInstallationCallback,
} from '../utils/githubAppInstall'

let processedInstallationId: string | null = null
let globalInstallConnecting = false
const installConnectingListeners = new Set<(connecting: boolean) => void>()

function setGlobalInstallConnecting(connecting: boolean) {
  globalInstallConnecting = connecting
  installConnectingListeners.forEach((listener) => listener(connecting))
}

/** Subscribe to GitHub App install connect in progress (handler runs app-wide). */
export function useGitHubInstallConnecting(): boolean {
  const [connecting, setConnecting] = useState(globalInstallConnecting)
  useEffect(() => {
    const listener = (value: boolean) => setConnecting(value)
    installConnectingListeners.add(listener)
    return () => {
      installConnectingListeners.delete(listener)
    }
  }, [])
  return connecting
}

/** Completes GitHub App install when the user returns with installation_id (any route). */
export function useGitHubInstallationCallback() {
  const [searchParams, setSearchParams] = useSearchParams()
  const { isAuthenticated, isLoading: authLoading, checkAuth } = useAuth()
  const { refetch } = useRepositories()
  const { showNotification } = useNotification()
  const [connecting, setConnecting] = useState(false)

  useEffect(() => {
    const detected = detectGitHubInstallationCallback(searchParams)
    if (!detected) return
    if (processedInstallationId === detected.installationId) return

    const installationId = detected.installationId
    processedInstallationId = installationId
    applyInstallDataCenter(searchParams)

    const run = async () => {
      setConnecting(true)
      setGlobalInstallConnecting(true)
      try {
        if (authLoading) {
          processedInstallationId = null
          setConnecting(false)
          setGlobalInstallConnecting(false)
          return
        }

        if (!isAuthenticated) {
          await checkAuth()
        }
        if (!getSessionToken()) {
          // User returned from GitHub install before signing in — wait for login.
          processedInstallationId = null
          setConnecting(false)
          setGlobalInstallConnecting(false)
          return
        }
        await connectGitHubInstallation(installationId, detected.state)
        clearPendingGitHubInstallation()
        await checkAuth()
        await refetch()
        showNotification('GitHub connected successfully', 'success')

        const next = new URLSearchParams(searchParams)
        next.delete('installation_id')
        next.delete('setup_action')
        next.delete('state')
        next.delete('region')
        setSearchParams(next, { replace: true })
      } catch (error) {
        processedInstallationId = null
        clearPendingGitHubInstallation()
        const message = error instanceof Error ? error.message : 'Failed to connect GitHub'
        showNotification(message, 'error')
      } finally {
        setConnecting(false)
        setGlobalInstallConnecting(false)
      }
    }

    void run()
  }, [searchParams, isAuthenticated, authLoading, checkAuth, refetch, setSearchParams, showNotification])

  return { connecting }
}
